import { useMemo, useState } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownward, CheckMark } from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';
import AutocompleteSearch from '@/components/AutocompleteSearch';
import { PopperOverride } from '@/components/PopperOverride';
import QuestionEntity from '@/modules/question/entity/Question';
import useQuestionPlayerResponse from '@/modules/user-survey/hooks/useQuestionPlayerResponse';
import QuestionPlayerContainer from '@/modules/user-survey/questions/components/QuestionPlayerContainer';
import { QuestionPlayerProps } from '@/modules/user-survey/questions/types';
import { ResourceResponseTypeEnum } from '@/types/schema';

type Option = { value: string; label: string; optionText: string };

const SingleSelectQuestionPlayer = (props: QuestionPlayerProps) => {
  const { t } = useTranslation();
  const [searchStr, setSearchStr] = useState('');

  const {
    variant,
    question,
    userSurveyQuestionSettings,
    error,
    useRichTextForComment,
    response,
    onChange,
    commentTitle,
    disabled,
    requireComments,
  } = props;
  const actions = useQuestionPlayerResponse({ response, onChange });

  const options = useMemo(() => {
    if (!question.options?.length) {
      return [];
    }
    return question.options
      .filter((option) =>
        option.optionText.toLocaleLowerCase().includes(searchStr.toLocaleLowerCase()),
      )
      .map((option) => ({
        value: option.optionId,
        label: QuestionEntity.questionTextLabel(option.optionText),
        optionText: option.optionText,
        id: option.optionId,
        name: option.optionText,
      }));
  }, [question.options, searchStr]);

  const onSelectChange = useEvent((_?: string | null, option?: Option | null) => {
    if (disabled) {
      return;
    }

    actions.onQuestionResponseChange({
      responseType: ResourceResponseTypeEnum.SingleSelect,
      optionResponse: option ? [{ optionId: option.value, optionText: option.optionText }] : null,
    });
  });

  const value = response?.questionSurveyResponse?.optionResponse?.[0]?.optionId || null;
  const selectedValue = useMemo(() => {
    return options.find((option) => option.id === value);
  }, [options, value]);

  const renderOption = (option: any) => {
    const isSelected = value === option.id;

    return (
      <Stack alignItems="center" className="mb-2 w-full py-10 px-12" justifyContent="between">
        <Typography
          className="truncate"
          color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
          variant="body-short-02">
          {option.label}
        </Typography>
        {isSelected ? <CheckMark className="h-20 w-20 ms-12" fillColorToken="icon-active" /> : null}
      </Stack>
    );
  };
  return (
    <QuestionPlayerContainer
      commentTitle={commentTitle}
      disabled={disabled}
      error={error}
      post={response?.post}
      question={question}
      requireComments={requireComments}
      useRichTextForComment={useRichTextForComment}
      userSurveyQuestionSettings={userSurveyQuestionSettings}
      variant={variant}
      onPostChange={actions.onPostChange}>
      <AutocompleteSearch
        PopperComponent={(val) => <PopperOverride placement="bottom-end" {...val} />}
        className={variant === 'form' ? 'mt-16' : 'mt-32'}
        closeIcon={null}
        data={options}
        data-id="feedback-single-select"
        defaultValue={selectedValue ?? undefined}
        fetchMore={undefined}
        getOptionLabel={(option) => QuestionEntity.questionTextLabel(option.optionText)}
        initialValue={selectedValue?.label}
        placeholder={'Select an option'}
        popupIcon={<ArrowIosDownward className="h-20 w-20" fillColorToken="icon-placeholder" />}
        renderers={{
          renderOption: renderOption,
        }}
        onChange={onSelectChange}
        onInputChange={setSearchStr}
      />
    </QuestionPlayerContainer>
  );
};

export default SingleSelectQuestionPlayer;
